import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { toast } from 'sonner';
import { formatDateInput } from '../lib/utils';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { TrendingUp, TrendingDown, ArrowLeftRight, Globe } from 'lucide-react';

// Helper to parse API error messages
const parseApiError = (error, defaultMessage) => {
  const detail = error?.response?.data?.detail;
  if (!detail) return defaultMessage;
  
  // If detail is a string, return it directly
  if (typeof detail === 'string') return detail;
  
  // If detail is an array (Pydantic validation errors), extract messages
  if (Array.isArray(detail)) {
    return detail.map(err => {
      if (typeof err === 'string') return err;
      if (err.msg) return err.msg;
      return JSON.stringify(err);
    }).join(', ');
  }
  
  // If detail is an object, try to get a message
  if (typeof detail === 'object') {
    return detail.msg || detail.message || JSON.stringify(detail);
  }
  
  return defaultMessage;
};

const AddTransactionDialog = ({ open, onOpenChange, editTransaction = null }) => {
  const { company } = useAuth();
  const [loading, setLoading] = useState(false);
  const [transactionType, setTransactionType] = useState('revenue');
  
  // Form state
  const [formData, setFormData] = useState({
    amount: '',
    date: formatDateInput(new Date()),
    description: '',
    category_id: '',
    payment_account_id: '',
    counterparty_id: '',
    payment_method: 'cash',
    to_account_id: '',
    notes: '',
    currency: company?.currency || 'USD'
  });
  
  // Data for dropdowns
  const [categories, setCategories] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [counterparties, setCounterparties] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    if (open) {
      loadData();
      
      // If editing, populate form with existing transaction data
      if (editTransaction) {
        setTransactionType(editTransaction.transaction_type || 'revenue');
        setFormData({
          amount: editTransaction.amount?.toString() || '',
          date: editTransaction.date || formatDateInput(new Date()),
          description: editTransaction.description || '',
          category_id: editTransaction.category_id || '',
          payment_account_id: editTransaction.payment_account_id || '',
          counterparty_id: editTransaction.counterparty_id || '',
          payment_method: editTransaction.payment_method || 'cash',
          to_account_id: editTransaction.to_account_id || '',
          notes: editTransaction.notes || '',
          currency: editTransaction.currency || company?.currency || 'USD'
        });
      } else {
        // Reset form for new transaction
        setFormData(prev => ({ ...prev, currency: company?.currency || 'USD' }));
      }
    }
  }, [open, company, editTransaction]);

  const loadData = async () => {
    try {
      const [catRes, accRes, cpRes, currRes] = await Promise.all([
        api.get('/categories'),
        api.get('/accounts'),
        api.get('/counterparties'),
        api.get('/currency/supported')
      ]);
      setCategories(catRes.data);
      setAccounts(accRes.data);
      setCounterparties(cpRes.data);
      setCurrencies(currRes.data || []);
      
      // Set default account (first cash/bank account)
      const cashAccount = accRes.data.find(a => 
        a.account_subtype === 'cash' || a.account_subtype === 'bank'
      );
      if (cashAccount) {
        setFormData(prev => ({ ...prev, payment_account_id: cashAccount.id }));
      }
    } catch (error) {
      console.error('Failed to load data:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      toast.error('Please enter a valid amount');
      return;
    }
    
    if (!formData.payment_account_id) {
      toast.error('Please select an account');
      return;
    }
    
    // Category is required only for new transactions (not edits)
    if (!editTransaction && transactionType !== 'transfer' && !formData.category_id) {
      toast.error('Please select a category');
      return;
    }
    
    if (transactionType === 'transfer' && !formData.to_account_id) {
      toast.error('Please select destination account');
      return;
    }
    
    setLoading(true);
    
    try {
      if (editTransaction) {
        // Update existing transaction - only include non-empty fields
        const updateData = {
          transaction_type: transactionType,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          date: formData.date,
          payment_account_id: formData.payment_account_id,
          payment_method: formData.payment_method
        };
        
        // Only include optional fields if they have values
        if (formData.description) updateData.description = formData.description;
        if (formData.category_id) updateData.category_id = formData.category_id;
        if (formData.counterparty_id) updateData.counterparty_id = formData.counterparty_id;
        if (formData.to_account_id) updateData.to_account_id = formData.to_account_id;
        if (formData.notes) updateData.notes = formData.notes;
        
        await api.put(`/transactions/${editTransaction.id}`, updateData);
        toast.success('Transaction updated successfully!');
      } else {
        // Create new transaction
        await api.post('/transactions', {
          transaction_type: transactionType,
          amount: parseFloat(formData.amount),
          currency: formData.currency,
          date: formData.date,
          description: formData.description,
          category_id: formData.category_id || null,
          payment_account_id: formData.payment_account_id,
          counterparty_id: formData.counterparty_id || null,
          payment_method: formData.payment_method,
          to_account_id: formData.to_account_id || null,
          notes: formData.notes
        });
        toast.success('Transaction created successfully!');
      }
      
      // Reset form first
      setFormData({
        amount: '',
        date: formatDateInput(new Date()),
        description: '',
        category_id: '',
        payment_account_id: formData.payment_account_id,
        counterparty_id: '',
        payment_method: 'cash',
        to_account_id: '',
        notes: '',
        currency: company?.currency || 'USD'
      });
      
      // Trigger refresh
      window.dispatchEvent(new CustomEvent(editTransaction ? 'transaction-updated' : 'transaction-created'));
      
      // Close dialog
      onOpenChange(false);
      
    } catch (error) {
      const errorMessage = parseApiError(error, `Failed to ${editTransaction ? 'update' : 'create'} transaction`);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const filteredCategories = categories.filter(c => {
    if (transactionType === 'revenue') return c.category_type === 'revenue';
    if (transactionType === 'expense') return c.category_type === 'expense';
    return false;
  });

  const cashBankAccounts = accounts.filter(a => 
    ['cash', 'bank', 'mobile_money'].includes(a.account_subtype)
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]" data-testid="add-transaction-dialog">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{ fontFamily: 'Space Grotesk' }}>
            {editTransaction ? 'Edit Transaction' : 'Add Transaction'}
          </DialogTitle>
        </DialogHeader>

        <Tabs value={transactionType} onValueChange={setTransactionType} data-testid="transaction-type-tabs">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="revenue" className="gap-2" data-testid="tab-revenue">
              <TrendingUp size={16} className="text-green-600" />
              Revenue
            </TabsTrigger>
            <TabsTrigger value="expense" className="gap-2" data-testid="tab-expense">
              <TrendingDown size={16} className="text-red-600" />
              Expense
            </TabsTrigger>
            <TabsTrigger value="transfer" className="gap-2" data-testid="tab-transfer">
              <ArrowLeftRight size={16} className="text-blue-600" />
              Transfer
            </TabsTrigger>
          </TabsList>

          <form onSubmit={handleSubmit} className="mt-6 space-y-4">
            {/* Amount and Currency */}
            <div className="grid grid-cols-3 gap-2">
              <div className="col-span-2">
                <Label htmlFor="amount">Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  placeholder="0.00"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="text-2xl numbered h-14 mt-1"
                  data-testid="amount-input"
                  autoFocus
                />
              </div>
              <div>
                <Label htmlFor="currency">Currency</Label>
                <Select
                  value={formData.currency}
                  onValueChange={(value) => setFormData({ ...formData, currency: value })}
                >
                  <SelectTrigger className="h-14 mt-1" data-testid="currency-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-[300px]">
                    {currencies.map((c) => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.symbol} {c.code}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Date */}
            <div>
              <Label htmlFor="date">Date</Label>
              <Input
                id="date"
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="mt-1"
                data-testid="date-input"
              />
            </div>

            {/* Category (not for transfers) */}
            {transactionType !== 'transfer' && (
              <div>
                <Label>Category</Label>
                <Select
                  value={formData.category_id}
                  onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="category-select">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {filteredCategories.map((cat) => (
                      <SelectItem key={cat.id} value={cat.id}>
                        {cat.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* From Account */}
            <div>
              <Label>{transactionType === 'transfer' ? 'From Account' : 'Account'}</Label>
              <Select
                value={formData.payment_account_id}
                onValueChange={(value) => setFormData({ ...formData, payment_account_id: value })}
              >
                <SelectTrigger className="mt-1" data-testid="account-select">
                  <SelectValue placeholder="Select account" />
                </SelectTrigger>
                <SelectContent>
                  {cashBankAccounts.map((acc) => (
                    <SelectItem key={acc.id} value={acc.id}>
                      {acc.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* To Account (only for transfers) */}
            {transactionType === 'transfer' && (
              <div>
                <Label>To Account</Label>
                <Select
                  value={formData.to_account_id}
                  onValueChange={(value) => setFormData({ ...formData, to_account_id: value })}
                >
                  <SelectTrigger className="mt-1" data-testid="to-account-select">
                    <SelectValue placeholder="Select destination account" />
                  </SelectTrigger>
                  <SelectContent>
                    {cashBankAccounts
                      .filter(a => a.id !== formData.payment_account_id)
                      .map((acc) => (
                        <SelectItem key={acc.id} value={acc.id}>
                          {acc.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Description */}
            <div>
              <Label htmlFor="description">Description (optional)</Label>
              <Input
                id="description"
                placeholder="What was this for?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="mt-1"
                data-testid="description-input"
              />
            </div>

            {/* Counterparty (optional, not for transfers) */}
            {transactionType !== 'transfer' && counterparties.length > 0 && (
              <div>
                <Label>{transactionType === 'revenue' ? 'Customer' : 'Supplier'} (optional)</Label>
                <Select
                  value={formData.counterparty_id || 'none'}
                  onValueChange={(value) => setFormData({ ...formData, counterparty_id: value === 'none' ? '' : value })}
                >
                  <SelectTrigger className="mt-1" data-testid="counterparty-select">
                    <SelectValue placeholder="Select..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {counterparties
                      .filter(c => 
                        transactionType === 'revenue' 
                          ? c.counterparty_type === 'customer'
                          : c.counterparty_type === 'supplier'
                      )
                      .map((cp) => (
                        <SelectItem key={cp.id} value={cp.id}>
                          {cp.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Actions */}
            <div className="flex justify-end gap-3 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="add-transaction-cancel-button"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                className="bg-[hsl(var(--brand-500))] hover:bg-[hsl(var(--brand-600))] text-white"
                disabled={loading}
                data-testid="add-transaction-submit-button"
              >
                {loading ? 'Saving...' : (editTransaction ? 'Update Transaction' : 'Save Transaction')}
              </Button>
            </div>
          </form>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
};

export default AddTransactionDialog;
